import { useCallback, useEffect, useRef, useState } from 'react';
import {
  enqueuePluginSlot,
  getActivePluginJobs,
} from '../services/contentPlugins';

const RENDERING_STATUSES = new Set(['pending', 'processing']);

function isRenderingStatus(status) {
  return RENDERING_STATUSES.has(status);
}

export function usePluginSlotJobs({ screenId, token }) {
  const [pendingBySlot, setPendingBySlot] = useState({});
  const pendingRef = useRef({});
  const clearedSlotsRef = useRef(new Set());

  const clearSlotPending = useCallback((slotId) => {
    delete pendingRef.current[slotId];
    setPendingBySlot((prev) => {
      if (!prev[slotId]) return prev;
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
  }, []);

  const setSlotPending = useCallback((slotId, patch) => {
    const next = { ...pendingRef.current[slotId], ...patch, slotId };
    pendingRef.current[slotId] = next;
    setPendingBySlot((prev) => ({
      ...prev,
      [slotId]: next,
    }));
  }, []);

  const syncActiveJobsOnce = useCallback(async () => {
    if (!screenId || !token) return;

    try {
      const res = await getActivePluginJobs(screenId, token);
      const jobs = res.data?.jobs || [];

      for (const job of jobs) {
        const slotId = Number(job.slotId);
        if (!slotId || !isRenderingStatus(job.status)) continue;
        setSlotPending(slotId, {
          jobId: job.jobId,
          pluginId: job.pluginId,
          config: job.config,
          status: job.status,
          lastError: job.lastError,
        });
      }
    } catch (err) {
      console.error('syncActiveJobsOnce failed:', err);
    }
  }, [screenId, token, setSlotPending]);

  const startPluginJob = useCallback(async ({
    slotId,
    pluginId,
    config,
    userId,
  }) => {
    const res = await enqueuePluginSlot({
      userId,
      screenId,
      slotId,
      pluginId,
      config,
    }, token);

    const jobId = res.data?.jobId;
    if (!jobId) {
      throw new Error('No render job was queued');
    }

    clearedSlotsRef.current.delete(slotId);
    setSlotPending(slotId, {
      jobId,
      pluginId,
      config,
      status: res.data?.status || 'pending',
      lastError: null,
    });

    return { jobId, status: res.data?.status || 'pending' };
  }, [screenId, token, setSlotPending]);

  const isSlotRendering = useCallback((slotId) => {
    const pending = pendingRef.current[slotId] || pendingBySlot[slotId];
    return Boolean(pending && isRenderingStatus(pending.status));
  }, [pendingBySlot]);

  const getSlotPending = useCallback((slotId) => (
    pendingRef.current[slotId] || pendingBySlot[slotId] || null
  ), [pendingBySlot]);

  const cancelPendingJobForSlot = useCallback((slotId) => {
    clearedSlotsRef.current.add(slotId);
    clearSlotPending(slotId);
  }, [clearSlotPending]);

  useEffect(() => {
    syncActiveJobsOnce();
  }, [syncActiveJobsOnce]);

  return {
    pendingBySlot,
    startPluginJob,
    syncActiveJobsOnce,
    isSlotRendering,
    getSlotPending,
    cancelPendingJobForSlot,
  };
}
